import React, { useEffect, useMemo, useState, useRef } from "react";
import imageCompression from "browser-image-compression";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { allAPiServicesCall } from "../../services/apiServices";
import { getAuthConfig, getFormDataConfig } from "../../services/apiUtils";
import CommonLayout from "../../layouts/CommonLayout";
import {
    FaMusic,
    FaPlus,
    FaUser,
    FaCompactDisc,
    FaMicrophone,
    FaFileAlt,
    FaLink,
    FaImage,
    FaCalendarAlt,
    FaClock,
    FaHashtag,
    FaKey,
    FaTags,
    FaToggleOn,
    FaSave,
    FaArrowLeft,
    FaExclamationCircle,
    FaCheck,
    FaInfoCircle,
    FaCloudUploadAlt,
    FaEdit,
    FaGuitar,
    FaHeadphones
} from "react-icons/fa";
import "./AddSong.css";

function csvToArray(str) {
    return (str || "").split(",").map(s => s.trim()).filter(Boolean);
}

const toNumberOrNull = (v) => (v === "" || v === undefined || v === null ? null : Number(v));

export default function AddSongPage() {
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);

    // Dropdown data
    const [artists, setArtists] = useState([]);
    const [albums, setAlbums] = useState([]);
    const [loadingArtists, setLoadingArtists] = useState(false);
    const [loadingAlbums, setLoadingAlbums] = useState(false);
    const [imageLoading, setImageLoading] = useState(false);
    const [error, setError] = useState(null);

    // Selected IDs
    const [artist_id, setArtistId] = useState("");
    const [album_id, setAlbumId] = useState("");

    // Search state for artist dropdown
    const [artistSearchQuery, setArtistSearchQuery] = useState("");
    const [showArtistDropdown, setShowArtistDropdown] = useState(false);
    const [selectedArtistName, setSelectedArtistName] = useState("");
    const artistDropdownRef = useRef(null);

    // Core identity/content
    const [song_name, setSongName] = useState("");
    const [title, setTitle] = useState("");
    const [lyrics, setLyrics] = useState("");
    const [description, setDescription] = useState("");
    const [song_meaning, setSongMeaning] = useState("");

    // Media / URLs
    const [audioPreviewUrl, setAudioPreviewUrl] = useState("");
    const [musicVideoUrl, setMusicVideoUrl] = useState("");
    const [coverImageFile, setCoverImageFile] = useState(null);

    // Dates / numbers
    const [releaseDate, setReleaseDate] = useState("");
    const [song_duration, setSongDuration] = useState("");
    const [bpm, setBpm] = useState("");
    const [musicalKey, setMusicalKey] = useState("");

    // Arrays (CSV)
    const [subGenres, setSubGenres] = useState("");
    const [writers, setWriters] = useState("");
    const [producers, setProducers] = useState("");
    const [tags, setTags] = useState("");
    const [moods, setMoods] = useState("");

    // Flags
    const [explicit, setExplicit] = useState(false);
    const [isSingle, setIsSingle] = useState(false);
    const [isFeatured, setIsFeatured] = useState(false);
    const [isTrending, setIsTrending] = useState(false);
    const [includeInMusicVideoSection, setIncludeInMusicVideoSection] = useState(false);

    // Genres
    const [genres, setGenres] = useState([]);
    const [selectedGenre, setSelectedGenre] = useState("");

    const handleGenreChange = (e) => {
        setSelectedGenre(e.target.value);
    };

    useEffect(() => {
        const fetchGenres = async () => {
            try {
                const response = await allAPiServicesCall.getGenres({}, getAuthConfig());
                setGenres(response.data || []);
            } catch (error) {
                console.error("Error fetching genres:", error);
            }
        };
        fetchGenres();
    }, []);

    const handleSongImageChange = async (e) => {
        setImageLoading(true);
        let imageFile = e.target.files[0];

        if (imageFile?.size > 5 * 1024 * 1024) {
            setError("Image size should not exceed 5 MB.");
            setImageLoading(false);
            return;
        }

        const options = {
            maxSizeMB: 1,
            maxWidthOrHeight: 1024,
            useWebWorker: true,
            fileType: imageFile.type,
        };

        try {
            const compressedFile = await imageCompression(imageFile, options);
            const formData = new FormData();
            formData.append("image", compressedFile);

            const response = await allAPiServicesCall.uploadSongImage(formData, getFormDataConfig(), navigate);
            setCoverImageFile(response.data.path);
            setError(null);
        } catch (error) {
            console.error("Error:", error);
            setError("Failed to Upload Song Image.");
        } finally {
            setImageLoading(false);
        }
    };

    // Debounced search for artists
    useEffect(() => {
        if (artistSearchQuery.trim()) {
            const timer = setTimeout(async () => {
                try {
                    setLoadingArtists(true);
                    const res = await allAPiServicesCall.listArtistsSimple(
                        { limit: 40, sort: "artist_name", q: artistSearchQuery },
                        getAuthConfig()
                    );
                    const rows = Array.isArray(res?.data) ? res.data : [];
                    setArtists(rows);
                } catch (e) {
                    console.error(e);
                    toast.error("Failed to search artists");
                } finally {
                    setLoadingArtists(false);
                }
            }, 500);
            return () => clearTimeout(timer);
        } else {
            (async () => {
                try {
                    setLoadingArtists(true);
                    const res = await allAPiServicesCall.listArtistsSimple(
                        { limit: 40, sort: "artist_name" },
                        getAuthConfig()
                    );
                    const rows = Array.isArray(res?.data) ? res.data : [];
                    setArtists(rows);
                } catch (e) {
                    console.error(e);
                    toast.error("Failed to load artists");
                } finally {
                    setLoadingArtists(false);
                }
            })();
        }
    }, [artistSearchQuery]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (artistDropdownRef.current && !artistDropdownRef.current.contains(event.target)) {
                setShowArtistDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Load albums when artist changes
    useEffect(() => {
        const idNum = Number(artist_id);
        if (!idNum) {
            setAlbums([]);
            setAlbumId("");
            return;
        }
        (async () => {
            try {
                setLoadingAlbums(true);
                const res = await allAPiServicesCall.listAlbumsByArtist(
                    idNum,
                    { limit: 500, sort: "album_name" },
                    getAuthConfig()
                );
                const rows = Array.isArray(res?.data) ? res.data : [];
                setAlbums(rows);
                setAlbumId("");
            } catch (e) {
                console.error(e);
                toast.error("Failed to load albums");
            } finally {
                setLoadingAlbums(false);
            }
        })();
    }, [artist_id]);

    const onSubmit = async (e) => {
        e.preventDefault();

        if (!song_name && !title) {
            return toast.error("Please enter at least a Song Name or Title");
        }
        const artistIdNum = toNumberOrNull(artist_id);
        if (!artistIdNum) {
            return toast.error("Please select an Artist");
        }
        const albumIdNum = toNumberOrNull(album_id) || undefined;

        setSubmitting(true);
        try {
            const form = new FormData();

            form.append("artist_id", String(artistIdNum));
            if (albumIdNum !== undefined) form.append("album_id", String(albumIdNum));

            if (song_name) form.append("song_name", song_name.trim());
            if (title) form.append("title", title.trim());

            if (lyrics) form.append("lyrics", lyrics);
            if (description) form.append("description", description);
            if (song_meaning) form.append("song_meaning", song_meaning);

            if (audioPreviewUrl) form.append("audioPreviewUrl", audioPreviewUrl.trim());
            if (musicVideoUrl) form.append("musicVideoUrl", musicVideoUrl.trim());
            if (coverImageFile) form.append("coverImage", coverImageFile);

            if (releaseDate) form.append("releaseDate", new Date(releaseDate).toISOString());
            if (song_duration) form.append("song_duration", String(song_duration));
            if (bpm) form.append("bpm", bpm);
            if (musicalKey) form.append("key", musicalKey);

            const genresArr = selectedGenre ? [selectedGenre] : [];
            const subGenresArr = csvToArray(subGenres);
            const writersArr = csvToArray(writers);
            const producersArr = csvToArray(producers);
            const tagsArr = csvToArray(tags);
            const moodsArr = csvToArray(moods);

            if (genresArr.length) form.append("genre", JSON.stringify(genresArr));
            if (subGenresArr.length) form.append("subGenres", JSON.stringify(subGenresArr));
            if (writersArr.length) form.append("writers", JSON.stringify(writersArr));
            if (producersArr.length) form.append("producers", JSON.stringify(producersArr));
            if (tagsArr.length) form.append("tags", JSON.stringify(tagsArr));
            if (moodsArr.length) form.append("moods", JSON.stringify(moodsArr));

            form.append("explicit", String(explicit));
            form.append("isSingle", String(isSingle));
            form.append("isFeatured", String(isFeatured));
            form.append("isTrending", String(isTrending));
            form.append("includeInMusicVideoSection", String(includeInMusicVideoSection));
            form.append("hasLyrics", String(!!lyrics));

            const { data, status } = await allAPiServicesCall.createSong(form, getAuthConfig());
            if (status >= 200 && data) {
                toast.success("Song created successfully!");
                navigate("/all-songs");
            } else {
                toast.error(data?.message || "Failed to create song");
            }
        } catch (err) {
            console.error(err);
            toast.error("Error creating song");
        } finally {
            setSubmitting(false);
        }
    };

    const albumOptions = useMemo(
        () => albums.map(al => ({ value: al.album_id, label: al.album_name || `Album ${al.album_id}` })),
        [albums]
    );

    const completedFields = [
        song_name, selectedArtistName, selectedGenre, lyrics
    ].filter(Boolean).length;

    return (
        <CommonLayout>
            <div className="add-song-page">
                {/* Back Button */}
                <button className="add-song-back-btn" onClick={() => navigate("/all-songs")}>
                    <FaArrowLeft /> Back to All Songs
                </button>

                {/* Header Section */}
                <div className="add-song-header">
                    <div className="add-song-header-content">
                        <div className="add-song-header-icon">
                            <FaPlus />
                        </div>
                        <div className="add-song-header-text">
                            <h1 className="add-song-title">
                                <FaMusic style={{ color: "#EC4899" }} />
                                Add New Song
                            </h1>
                            <p className="add-song-subtitle">
                                Create a new song entry with all its details
                            </p>
                        </div>
                    </div>
                    <div className="add-song-stats">
                        <div className="add-song-stat-card">
                            <FaCheck className="add-song-stat-icon" />
                            <div className="add-song-stat-info">
                                <span className="add-song-stat-value">{completedFields}/4</span>
                                <span className="add-song-stat-label">Required</span>
                            </div>
                        </div>
                        <div className="add-song-stat-card">
                            <FaTags className="add-song-stat-icon" />
                            <div className="add-song-stat-info">
                                <span className="add-song-stat-value">{genres.length}</span>
                                <span className="add-song-stat-label">Genres</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={onSubmit}>
                    <div className="add-song-form-container">
                        {/* Artist & Album Section */}
                        <div className="add-song-section">
                            <div className="add-song-section-header">
                                <div className="add-song-section-icon">
                                    <FaUser />
                                </div>
                                <div>
                                    <h2 className="add-song-section-title">Artist & Album</h2>
                                    <p className="add-song-section-subtitle">Select the artist and optionally an album</p>
                                </div>
                            </div>

                            <div className="add-song-form-grid">
                                {/* Artist Search */}
                                <div className="add-song-form-group" ref={artistDropdownRef}>
                                    <label className="add-song-label">
                                        Artist <span className="add-song-label-required">*</span>
                                    </label>
                                    <div className="add-song-artist-search">
                                        <div className="add-song-input-wrapper">
                                            <input
                                                type="text"
                                                className="add-song-input"
                                                placeholder="Search for an artist..."
                                                value={selectedArtistName || artistSearchQuery}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setArtistSearchQuery(value);
                                                    setSelectedArtistName("");
                                                    setArtistId("");
                                                    setShowArtistDropdown(true);
                                                }}
                                                onFocus={() => setShowArtistDropdown(true)}
                                                required
                                                autoComplete="off"
                                            />
                                            {loadingArtists && <div className="add-song-input-spinner"></div>}
                                        </div>

                                        {showArtistDropdown && artists.length > 0 && (
                                            <div className="add-song-artist-dropdown">
                                                {artists.map(artist => (
                                                    <div
                                                        key={artist.artist_id}
                                                        className="add-song-artist-option"
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            setArtistId(artist.artist_id);
                                                            setSelectedArtistName(artist.artist_name || artist.artist || `Artist ${artist.artist_id}`);
                                                            setArtistSearchQuery("");
                                                            setShowArtistDropdown(false);
                                                        }}
                                                    >
                                                        <div className="add-song-artist-option-avatar">
                                                            <FaUser />
                                                        </div>
                                                        <span className="add-song-artist-option-name">
                                                            {artist.artist_name || artist.artist || `Artist ${artist.artist_id}`}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {showArtistDropdown && artists.length === 0 && !loadingArtists && artistSearchQuery && (
                                            <div className="add-song-artist-dropdown">
                                                <div className="add-song-artist-no-results">
                                                    No artists found for "{artistSearchQuery}"
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Album Select */}
                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        Album <span className="add-song-label-optional">(optional)</span>
                                    </label>
                                    <select
                                        className="add-song-select"
                                        value={album_id}
                                        onChange={(e) => setAlbumId(e.target.value)}
                                        disabled={!artist_id || loadingAlbums}
                                    >
                                        <option value="">
                                            {artist_id
                                                ? (loadingAlbums ? "Loading albums..." : "Select album (optional)")
                                                : "Select artist first"}
                                        </option>
                                        {albumOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Song Identity Section */}
                        <div className="add-song-section">
                            <div className="add-song-section-header">
                                <div className="add-song-section-icon">
                                    <FaMicrophone />
                                </div>
                                <div>
                                    <h2 className="add-song-section-title">Song Identity</h2>
                                    <p className="add-song-section-subtitle">Basic information about the song</p>
                                </div>
                            </div>

                            <div className="add-song-form-grid">
                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        Song Name <span className="add-song-label-required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="add-song-input"
                                        placeholder="Enter song name..."
                                        value={song_name}
                                        onChange={e => setSongName(e.target.value)}
                                    />
                                </div>

                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        Title <span className="add-song-label-optional">(optional if song name provided)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="add-song-input"
                                        placeholder="Enter title..."
                                        value={title}
                                        onChange={e => setTitle(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Content Section */}
                        <div className="add-song-section">
                            <div className="add-song-section-header">
                                <div className="add-song-section-icon">
                                    <FaFileAlt />
                                </div>
                                <div>
                                    <h2 className="add-song-section-title">Content</h2>
                                    <p className="add-song-section-subtitle">Lyrics, description, and meaning</p>
                                </div>
                            </div>

                            <div className="add-song-form-grid single-column">
                                <div className="add-song-form-group add-song-form-full">
                                    <label className="add-song-label">
                                        <FaEdit /> Lyrics
                                    </label>
                                    <textarea
                                        className="add-song-textarea lyrics-textarea"
                                        placeholder="Enter song lyrics..."
                                        value={lyrics}
                                        onChange={e => setLyrics(e.target.value)}
                                        rows={8}
                                    />
                                </div>

                                <div className="add-song-form-group add-song-form-full">
                                    <label className="add-song-label">Description</label>
                                    <textarea
                                        className="add-song-textarea"
                                        placeholder="Enter song description..."
                                        value={description}
                                        onChange={e => setDescription(e.target.value)}
                                        rows={4}
                                    />
                                </div>

                                <div className="add-song-form-group add-song-form-full">
                                    <label className="add-song-label">Song Meaning</label>
                                    <textarea
                                        className="add-song-textarea"
                                        placeholder="What does this song mean?"
                                        value={song_meaning}
                                        onChange={e => setSongMeaning(e.target.value)}
                                        rows={4}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Media Section */}
                        <div className="add-song-section">
                            <div className="add-song-section-header">
                                <div className="add-song-section-icon">
                                    <FaLink />
                                </div>
                                <div>
                                    <h2 className="add-song-section-title">Media & Links</h2>
                                    <p className="add-song-section-subtitle">Audio preview, video, and cover image</p>
                                </div>
                            </div>

                            <div className="add-song-form-grid">
                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        <FaHeadphones /> Audio Preview URL
                                    </label>
                                    <input
                                        type="url"
                                        className="add-song-input"
                                        placeholder="https://..."
                                        value={audioPreviewUrl}
                                        onChange={e => setAudioPreviewUrl(e.target.value)}
                                    />
                                </div>

                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        <FaCompactDisc /> Music Video URL
                                    </label>
                                    <input
                                        type="url"
                                        className="add-song-input"
                                        placeholder="https://youtube.com/..."
                                        value={musicVideoUrl}
                                        onChange={e => setMusicVideoUrl(e.target.value)}
                                    />
                                </div>

                                <div className="add-song-form-group add-song-form-full">
                                    <label className="add-song-label">
                                        <FaImage /> Cover Image
                                    </label>
                                    <div className={`add-song-file-upload ${coverImageFile ? 'has-file' : ''}`}>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleSongImageChange}
                                        />
                                        {coverImageFile ? (
                                            <div className="add-song-file-preview">
                                                <img
                                                    src={coverImageFile}
                                                    alt="Cover preview"
                                                    className="add-song-file-preview-image"
                                                />
                                                <div className="add-song-file-preview-info">
                                                    <p className="add-song-file-preview-name">Image uploaded</p>
                                                    <span className="add-song-file-preview-change">Click to change</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <FaCloudUploadAlt className="add-song-file-upload-icon" />
                                                <p className="add-song-file-upload-text">
                                                    Click or drag to upload cover image
                                                </p>
                                                <p className="add-song-file-upload-hint">
                                                    PNG, JPG up to 5MB. Will be compressed to 1MB.
                                                </p>
                                            </>
                                        )}
                                    </div>
                                    {imageLoading && (
                                        <div className="add-song-image-loading">
                                            <div className="add-song-image-loading-spinner"></div>
                                            <span className="add-song-image-loading-text">Uploading image...</span>
                                        </div>
                                    )}
                                    {error && (
                                        <div className="add-song-error">
                                            <FaExclamationCircle />
                                            {error}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Technical Details Section */}
                        <div className="add-song-section">
                            <div className="add-song-section-header">
                                <div className="add-song-section-icon">
                                    <FaClock />
                                </div>
                                <div>
                                    <h2 className="add-song-section-title">Technical Details</h2>
                                    <p className="add-song-section-subtitle">Release date, duration, and audio properties</p>
                                </div>
                            </div>

                            <div className="add-song-form-grid three-columns">
                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        <FaCalendarAlt /> Release Date
                                    </label>
                                    <input
                                        type="date"
                                        className="add-song-input"
                                        value={releaseDate}
                                        onChange={e => setReleaseDate(e.target.value)}
                                    />
                                </div>

                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        <FaClock /> Duration (seconds)
                                    </label>
                                    <input
                                        type="number"
                                        className="add-song-input"
                                        placeholder="e.g., 240"
                                        value={song_duration}
                                        onChange={e => setSongDuration(e.target.value)}
                                    />
                                </div>

                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        <FaHashtag /> BPM
                                    </label>
                                    <input
                                        type="text"
                                        className="add-song-input"
                                        placeholder="e.g., 120"
                                        value={bpm}
                                        onChange={e => setBpm(e.target.value)}
                                    />
                                </div>

                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        <FaKey /> Musical Key
                                    </label>
                                    <input
                                        type="text"
                                        className="add-song-input"
                                        placeholder="e.g., C#m, G major"
                                        value={musicalKey}
                                        onChange={e => setMusicalKey(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Categorization Section */}
                        <div className="add-song-section">
                            <div className="add-song-section-header">
                                <div className="add-song-section-icon">
                                    <FaTags />
                                </div>
                                <div>
                                    <h2 className="add-song-section-title">Categorization</h2>
                                    <p className="add-song-section-subtitle">Genres, tags, moods, and credits</p>
                                </div>
                            </div>

                            <div className="add-song-form-grid">
                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        Genre <span className="add-song-label-required">*</span>
                                    </label>
                                    <select
                                        className="add-song-select"
                                        value={selectedGenre}
                                        onChange={handleGenreChange}
                                        required
                                    >
                                        <option value="">Select Genre</option>
                                        {genres.map((genre) => (
                                            <option key={genre._id} value={genre.genreName}>
                                                {genre.genreName}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        Sub-Genres <span className="add-song-label-optional">(comma separated)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="add-song-input"
                                        placeholder="e.g., Pop Rock, Indie"
                                        value={subGenres}
                                        onChange={e => setSubGenres(e.target.value)}
                                    />
                                </div>

                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        <FaGuitar /> Writers <span className="add-song-label-optional">(comma separated)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="add-song-input"
                                        placeholder="e.g., John Doe, Jane Smith"
                                        value={writers}
                                        onChange={e => setWriters(e.target.value)}
                                    />
                                </div>

                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        Producers <span className="add-song-label-optional">(comma separated)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="add-song-input"
                                        placeholder="e.g., Max Martin"
                                        value={producers}
                                        onChange={e => setProducers(e.target.value)}
                                    />
                                </div>

                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        Tags <span className="add-song-label-optional">(comma separated)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="add-song-input"
                                        placeholder="e.g., love, summer, dance"
                                        value={tags}
                                        onChange={e => setTags(e.target.value)}
                                    />
                                </div>

                                <div className="add-song-form-group">
                                    <label className="add-song-label">
                                        Moods <span className="add-song-label-optional">(comma separated)</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="add-song-input"
                                        placeholder="e.g., happy, energetic, romantic"
                                        value={moods}
                                        onChange={e => setMoods(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Flags Section */}
                        <div className="add-song-section">
                            <div className="add-song-section-header">
                                <div className="add-song-section-icon">
                                    <FaToggleOn />
                                </div>
                                <div>
                                    <h2 className="add-song-section-title">Visibility & Flags</h2>
                                    <p className="add-song-section-subtitle">Special attributes for the song</p>
                                </div>
                            </div>

                            <div className="add-song-flags-grid">
                                <label className={`add-song-flag ${explicit ? 'active' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={explicit}
                                        onChange={(e) => setExplicit(e.target.checked)}
                                    />
                                    <div className="add-song-flag-toggle"></div>
                                    <div className="add-song-flag-content">
                                        <span className="add-song-flag-label">Explicit</span>
                                        <span className="add-song-flag-description">Contains explicit content</span>
                                    </div>
                                </label>

                                <label className={`add-song-flag ${isSingle ? 'active' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={isSingle}
                                        onChange={(e) => setIsSingle(e.target.checked)}
                                    />
                                    <div className="add-song-flag-toggle"></div>
                                    <div className="add-song-flag-content">
                                        <span className="add-song-flag-label">Single</span>
                                        <span className="add-song-flag-description">Released as a single</span>
                                    </div>
                                </label>

                                <label className={`add-song-flag ${isFeatured ? 'active' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={isFeatured}
                                        onChange={(e) => setIsFeatured(e.target.checked)}
                                    />
                                    <div className="add-song-flag-toggle"></div>
                                    <div className="add-song-flag-content">
                                        <span className="add-song-flag-label">Featured</span>
                                        <span className="add-song-flag-description">Show in featured section</span>
                                    </div>
                                </label>

                                <label className={`add-song-flag ${isTrending ? 'active' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={isTrending}
                                        onChange={(e) => setIsTrending(e.target.checked)}
                                    />
                                    <div className="add-song-flag-toggle"></div>
                                    <div className="add-song-flag-content">
                                        <span className="add-song-flag-label">Trending</span>
                                        <span className="add-song-flag-description">Show in trending section</span>
                                    </div>
                                </label>

                                <label className={`add-song-flag ${includeInMusicVideoSection ? 'active' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={includeInMusicVideoSection}
                                        onChange={(e) => setIncludeInMusicVideoSection(e.target.checked)}
                                    />
                                    <div className="add-song-flag-toggle"></div>
                                    <div className="add-song-flag-content">
                                        <span className="add-song-flag-label">Music Video</span>
                                        <span className="add-song-flag-description">Include in video section</span>
                                    </div>
                                </label>
                            </div>
                        </div>

                        {/* Submit Section */}
                        <div className="add-song-submit-section">
                            <div className="add-song-submit-hint">
                                <FaInfoCircle />
                                <span>Fields marked with * are required</span>
                            </div>
                            <div className="add-song-submit-actions">
                                <button
                                    type="button"
                                    className="add-song-btn add-song-btn-secondary"
                                    onClick={() => navigate("/all-songs")}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="add-song-btn add-song-btn-primary"
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <>Saving...</>
                                    ) : (
                                        <>
                                            <FaSave /> Save Song
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </CommonLayout>
    );
}
